import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTransferLogs } from '../../../slices/company/finalProductSlice';
import { ArrowLeft, ArrowLeftRight, Calendar, Search, ArrowRight, CornerDownRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { customTableStyles } from "../../../utils/tableStylings";
import { API_URL } from '../../../utils/constant';

const TransferLogsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { transferLogs, loading } = useSelector((state) => state.finalProduct);
  const [searchTerm, setSearchTerm] = useState('');
  const [directionFilter, setDirectionFilter] = useState('ALL');

  useEffect(() => {
    const company = JSON.parse(localStorage.getItem('company'));
    const companyId = company?.company?.id;
    if (companyId) {
      dispatch(fetchTransferLogs(companyId));
    }
  }, [dispatch]);

  const filteredLogs = (transferLogs || []).filter((log) => {
    const nameMatch = (log.product_name || log.product?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.variant_name || log.variation?.variant_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.variation?.serial_no || '').toLowerCase().includes(searchTerm.toLowerCase());

    const dirMatch = directionFilter === 'ALL' ||
      log.direction === directionFilter ||
      (directionFilter === 'loose_transfers' && (log.direction === 'to_loose' || log.direction === 'retail_to_loose' || log.direction === 'wholesale_to_loose'));

    return nameMatch && dirMatch;
  });

  const columns = [
    {
      name: 'Product Details',
      selector: (row) => row.product_name || row.product?.name || '',
      sortable: true,
      grow: 2,
      cell: (row) => {
        const image = row.product?.product_image;
        return (
          <div className="d-flex align-items-center gap-2 py-2">
            <img
              src={image ? `${API_URL}/${image}` : './logo.png'}
              alt="product"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = './logo.png';
              }}
              style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, border: '1px solid #e2e8f0' }}
            />
            <div>
              <div className="fw-bold text-dark">{row.product_name || row.product?.name || 'Unnamed Product'}</div>
              {row.product?.sku && <small className="text-muted">SKU: {row.product.sku}</small>}
            </div>
          </div>
        );
      }
    },
    {
      name: 'Variation / Serial',
      selector: (row) => row.variant_name || '',
      sortable: true,
      cell: (row) => (
        <div>
          <div className="fw-semibold text-secondary">{row.variant_name || row.variation?.variant_name || 'Standard'}</div>
          {row.variation?.serial_no && (
            <small className="text-muted d-block">Serial: {row.variation.serial_no}</small>
          )}
        </div>
      )
    },
    {
      name: 'Transfer Direction',
      selector: (row) => row.direction,
      sortable: true,
      cell: (row) => {
        if (row.direction === 'wholesale_to_loose') {
          return (
            <span className="badge bg-purple-subtle text-purple border border-purple-subtle px-2.5 py-1 fw-bold fs-12 d-flex align-items-center gap-1" style={{ backgroundColor: '#f3e8ff', color: '#7e22ce', borderColor: '#d8b4fe' }}>
              Wholesale → Loose (JNM LOOSE)
            </span>
          );
        }
        if (row.direction === 'to_loose' || row.direction === 'retail_to_loose') {
          return (
            <span className="badge bg-info-subtle text-info border border-info-subtle px-2.5 py-1 fw-bold fs-12 d-flex align-items-center gap-1">
              Retail → Loose (JNM LOOSE)
            </span>
          );
        }
        const isW2R = row.direction === 'wholesale_to_retail';
        return (
          <span className={`badge ${isW2R ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-warning-subtle text-warning border border-warning-subtle'} px-2.5 py-1 fw-bold fs-12 d-flex align-items-center gap-1`}>
            {isW2R ? 'Wholesale → Retail' : 'Retail → Wholesale'}
          </span>
        );
      }
    },
    {
      name: 'Qty Transferred',
      selector: (row) => row.qty_transferred,
      sortable: true,
      center: true,
      cell: (row) => (
        <span className="badge bg-primary text-white rounded-pill px-3 py-1.5 fs-13">
          {row.qty_transferred}
        </span>
      )
    },
    {
      name: 'Retail Stock History',
      cell: (row) => (
        <div className="text-center py-1">
          <div className="small text-muted">Before: <span className="fw-bold text-dark">{row.retail_qty_before ?? 0}</span></div>
          <div className="small text-muted mt-0.5">After: <span className="fw-bold text-primary">{row.retail_qty_after ?? 0}</span></div>
        </div>
      )
    },
    {
      name: 'Wholesale / Loose History',
      cell: (row) => {
        const isLoose = row.direction === 'to_loose' || row.direction === 'retail_to_loose' || row.direction === 'wholesale_to_loose';
        return (
          <div className="text-center py-1">
            <div className="small text-muted">{isLoose ? 'Loose Before: ' : 'Wholesale Before: '}<span className="fw-bold text-dark">{row.wholesale_qty_before ?? 0}</span></div>
            <div className="small text-muted mt-0.5">{isLoose ? 'Loose After: ' : 'Wholesale After: '}<span className="fw-bold text-success">{row.wholesale_qty_after ?? 0}</span></div>
          </div>
        );
      }
    },
    {
      name: 'Logged At',
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => (
        <div className="small text-muted">
          <div className="d-flex align-items-center gap-1">
            <Calendar size={12} />
            {new Date(row.createdAt).toLocaleDateString('en-IN')}
          </div>
          <div className="mt-0.5">{new Date(row.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
      )
    }
  ];

  return (
    <div className="row">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded-3 shadow-sm border">
        <div>
          <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
            <ArrowLeftRight className="text-info" size={24} />
            Stock Transfer History Logs
          </h4>
          <p className="text-muted small mb-0">
            Audit trail of wholesale-to-retail, retail-to-wholesale, and retail/wholesale-to-loose stock conversions.
          </p>
        </div>
        <button
          className="btn btn-outline-secondary d-flex align-items-center gap-1.5"
          onClick={() => navigate('/products')}
        >
          <ArrowLeft size={16} />
          Back to Products
        </button>
      </div>

      {/* Filter Section */}
      <div className="card border-0 shadow-sm rounded-3 mb-4">
        <div className="card-body p-3">
          <div className="row g-3 align-items-center">
            {/* Search Bar */}
            <div className="col-md-5 col-lg-4">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <Search size={16} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Search by Product Name, Variant, Serial..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Direction Filter Tabs */}
            <div className="col-md-7 col-lg-8 d-flex justify-content-md-end flex-wrap gap-1">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm px-3 ${directionFilter === 'ALL' ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setDirectionFilter('ALL')}
                >
                  All Transfers
                </button>
                <button
                  type="button"
                  className={`btn btn-sm px-3 ${directionFilter === 'wholesale_to_retail' ? 'btn-success text-white' : 'btn-outline-success'}`}
                  onClick={() => setDirectionFilter('wholesale_to_retail')}
                >
                  Wholesale → Retail
                </button>
                <button
                  type="button"
                  className={`btn btn-sm px-3 ${directionFilter === 'retail_to_wholesale' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setDirectionFilter('retail_to_wholesale')}
                >
                  Retail → Wholesale
                </button>
                <button
                  type="button"
                  className={`btn btn-sm px-3 ${directionFilter === 'retail_to_loose' ? 'btn-info text-white' : 'btn-outline-info'}`}
                  onClick={() => setDirectionFilter('retail_to_loose')}
                >
                  Retail → Loose
                </button>
                <button
                  type="button"
                  className={`btn btn-sm px-3 ${directionFilter === 'wholesale_to_loose' ? 'btn-secondary text-white' : 'btn-outline-secondary'}`}
                  onClick={() => setDirectionFilter('wholesale_to_loose')}
                >
                  Wholesale → Loose
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="card border-0 shadow-sm rounded-3">
        <div className="card-body p-0">
          <DataTable
            columns={columns}
            data={filteredLogs}
            progressPending={loading}
            progressComponent={
              <div className="text-center py-5">
                <div className="spinner-border text-info" role="status"></div>
                <p className="mt-2 text-muted">Retrieving audit history logs...</p>
              </div>
            }
            pagination
            striped
            highlightOnHover
            customStyles={customTableStyles}
            noDataComponent={
              <div className="text-center py-5">
                <ArrowLeftRight size={48} className="text-muted opacity-30 mb-2" />
                <h6 className="fw-semibold text-muted">No Stock Transfers Recorded</h6>
                <p className="text-muted small">Any future W→R or R→W conversions will appear here.</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default TransferLogsPage;
